import React, { useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { useAppDispatch } from 'hooks';
import t from 'locales/en/main.json';

import { Base64ToBlob } from 'utils/Base64ToBlob';

import { setErrorToast } from '../../store/toast';

import { IImagePicker } from './ImagePicker';
import {
  ArrowButton,
  CameraButton,
  CameraContainer,
  ExitButton,
  ImageButton,
  ImagePickerContainer,
  ImagePickerHeader,
  ImagePickerModal,
  ImagePickerTitle,
  InputImage,
  RotatePhone,
} from './ImagePickerElements';

const videoConstraints = {
  facingMode: 'environment',
};

export const ImagePicker: React.FC<IImagePicker.IProps> = (props) => {
  const { showPickerModal, onCloseModal, onChooseImage } = props;
  const dispatch = useAppDispatch();
  const [showCamera, setShowCamera] = useState(false);
  const onClickShowCamera = () => setShowCamera((state) => !state);
  const webcamRef: React.MutableRefObject<any> = React.useRef(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const onButtonClick = () => {
    inputFileRef?.current?.click();
  };

  const isImageSizeAccepted = (width: number, height: number) => {
    return Math.floor((height / width) * 10) === 7;
  };

  const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file) {
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function (e) {
        const image = new Image();
        image.src = `${e?.target?.result}`;
        image.onload = () => {
          if (isImageSizeAccepted(image.width, image.height)) {
            onChooseImage(file);
          } else {
            dispatch(setErrorToast({ title: '', message: t.image_picker.image_size_error }));
          }
        };
      };
    }
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    const file = Base64ToBlob(imageSrc);
    onChooseImage(file);
    onClickShowCamera();
    onCloseModal();
  };

  return (
    <>
      <InputImage
        type="file"
        id="file"
        ref={inputFileRef}
        accept="image/*"
        onChange={onChangeFile}
      />
      {showCamera && (
        <>
          <RotatePhone>{t.image_picker.rotate_device}</RotatePhone>
          <CameraContainer>
            <ExitButton onClick={onClickShowCamera}>{t.image_picker.exit}</ExitButton>
            <Webcam
              className={'camera'}
              ref={webcamRef}
              screenshotFormat={'image/jpeg'}
              minScreenshotHeight={768}
              minScreenshotWidth={1024}
              screenshotQuality={1}
              videoConstraints={videoConstraints}
            />
            <CameraButton onClick={capture} />
          </CameraContainer>
        </>
      )}
      <ImagePickerModal showModal={showPickerModal} onClick={onCloseModal}>
        <ImagePickerContainer
          showModal={showPickerModal}
          onClick={(event) => event.stopPropagation()}
        >
          <ImagePickerHeader>
            <ImagePickerTitle>{t.image_picker.upload_a_photo}</ImagePickerTitle>
            <ArrowButton className={'icon-Big-Arrows'} onClick={onCloseModal} />
          </ImagePickerHeader>
          <ImageButton onClick={onButtonClick}>
            <label>{t.image_picker.choose_form_gallery}</label>
            <span className={'icon-gallery-add'} />
          </ImageButton>
          <ImageButton onClick={onClickShowCamera}>
            <label>{t.image_picker.take_a_picture}</label>
            <span className={'icon-u_camera-plus'} />
          </ImageButton>
        </ImagePickerContainer>
      </ImagePickerModal>
    </>
  );
};
